import React, { CSSProperties, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import uniqueId from 'lodash/uniqueId'

import { Field, ImgEasyResult, Label, ModalGrid, ModalProps, SimpleInput, UploadEasyCrop } from '@makyoapp/makyo-ui'

import { useDebounceInput, useFormEnter } from '@/shared/makyo-hooks'
import { appConfig } from '@/configs'
import { cleanUpString } from '@/shared/utils'

interface BasicInfoEditModalProps {
  id?: string
  show: boolean
  name: string
  avatar?: string
  title?: string
  withAvatar?: boolean
  isLoading?: boolean
  bodyClassName?: string
  bodyStyle?: CSSProperties
  isVertical?: boolean
  header?: React.ReactNode
  nameLabel?: React.ReactNode
  submitLabel?: React.ReactNode
  modalProps?: ModalProps
  onHide?: () => void
  onSubmit: (nextName: string, avatar?: ImgEasyResult, done?: () => void) => void
  verifyName?: (name: string) => string
}

/**
 * This modal should be lazy-loaded using (`src/lib/lazyload/lazy-modal`)
 */
export default function BasicInfoEditModal(props: BasicInfoEditModalProps) {
  const { t } = useTranslation()
  const { show, onHide, name, avatar, onSubmit } = props

  const [debounceInput, cancelDebounceInput, setDebounceInput, activeName, setActiveName] = useDebounceInput(name)

  const [activeAvatar, setActiveAvatar] = useState<ImgEasyResult | undefined>(
    avatar
      ? {
          src: avatar,
          file: undefined,
          crop: undefined,
        }
      : undefined
  )

  const handleSubmit = useCallback(() => {
    onSubmit(
      activeName,
      activeAvatar,
      //typeof activeAvatar === 'object' ? activeAvatar : undefined,
      () => {
        setActiveName('')
        cancelDebounceInput()
        setDebounceInput('')
        setActiveAvatar(undefined)
      }
    )
  }, [onSubmit, activeName, activeAvatar, setActiveAvatar, setDebounceInput, cancelDebounceInput, setActiveName])

  const formId = props.id ? props.id : uniqueId('basic-info-modal-')

  const [nameError, setNameError] = useState('')

  useFormEnter(formId, () => {
    handleSubmit()
  })

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (!!activeName) {
      timeout = setTimeout(() => {
        setActiveName(cleanUpString(activeName))
      }, 1000)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [activeName, setActiveName])

  const charCount = activeName.trim().length
  const validLength = charCount >= appConfig.charConstraints.min && charCount < appConfig.charConstraints.max

  return (
    <ModalGrid
      modalId={props.id ? `${props.id}-modal` : undefined}
      show={show}
      onHide={onHide}
      onCancel={onHide}
      title={props.title ?? ''}
      showHeader={!props.header}
      primaryType='submit'
      isLoading={props.isLoading}
      disabled={!validLength}
      onConfirm={handleSubmit}
      bodyProps={{ className: props.bodyClassName ? props.bodyClassName : 'modal-md' }}
      {...props.modalProps}
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        {props.header}
        <Field isVertical={props.isVertical}>
          {props.nameLabel || <Label required>{t('Name')}</Label>}
          <SimpleInput
            id={props.id ? `${props.id}-name` : undefined}
            error={nameError ? t(nameError) : undefined}
            required={true}
            placeholder={t('Please Enter Name')}
            value={activeName}
            onChange={(e) => {
              let val = e.currentTarget.value.replaceAll(/[^A-Z0-9.,'\s]+/gim, '')
              val = val.replaceAll(/^[.,']/gim, '')
              setActiveName(val)
            }}
            type='text'
            withCounterConfig={appConfig.charConstraints}
          />
        </Field>
        {props.withAvatar !== false && (
          <Field isVertical={props.isVertical}>
            <Label htmlFor='avatar'>{t('Avatar')}</Label>
            <UploadEasyCrop
              id={props.id ? `${props.id}-avatar` : undefined}
              initialPreview={activeAvatar?.src}
              onChange={(nextResult) => {
                if (nextResult) {
                  setActiveAvatar(nextResult)
                } else {
                  setActiveAvatar(undefined)
                }
              }}
              supportedExtensions={[]}
            />
          </Field>
        )}
      </form>
    </ModalGrid>
  )
}
